import React from "react";
import { atom } from "recoil";
import { GetModelsQuery } from "../containers/model-library/types/queries.generated";
import { SimulatorOutput } from "../containers/simulator/services/inputs";
import { PackDosageUnit, RoundingOptionLabels } from "../models/simulator/options";

export const pageSizeOptions = [25, 50, 100, 250];

type ActivePageType = "simulations.activePage";
type RowsPerPageType = "simulations.rowsPerPage";

type ActivePage = {
	key: ActivePageType;
	default: number;
};

type RowsPerPage = {
	key: RowsPerPageType;
	default: number;
};

type measureUnitsType = {
	key: "measureUnits";
	default: {
		currency: string;
		packDosage: PackDosageUnit;
		patientWeight: string;
	};
};

type modelInputsType = {
	key: "modelInputs";
	default: modelInput[];
};

type lockedSectionsType = {
	key: "lockedSections";
	default: {
		standardInputs: string[];
		sections: sectionType[];
	};
};

type sectionType = {
	depth: number;
	sectionIndex: number;
	isLocked: boolean;
	lockedFields: string[];
};

type modelInput = {
	id: string;
	title: string;
	tagType: string;
	description: string;
	path: string;
	component: JSX.Element;
	disabled?: boolean;
};

type simulationOutput = {
	simulationOutput?: SimulatorOutput;
	filteredSimulationOutput?: SimulatorOutput;
};

type commentInputsType = {
	key: "commentInputs";
	default: Record<string, never>;
};

type CachedModelType = {
	key: "cachedModel";
	default: Array<Pick<GetModelsQuery["GetModels"]["models"][0], "id" | "summary">>;
};

type SimulatorOutputType = {
	key: "simulatorOutput";
	default: simulationOutput;
};

type DiscontinuationType = {
	key: "isDiscontinuationDisabled";
	default: boolean;
};

type RoundingType = {
	key: "rounding";
	default: RoundingOptionLabels;
};

type SnackbarUpdateStatus = {
	key: "snackbarUpdateStatus";
	default: {
		hasError: boolean;
		simulationId: string[];
	};
};

type ComparisonFiltersType = {
	key: string;
	default: { duration: number | null; calendarYear: number | null; indications: string[] };
};

type ComparisonSimulationsType = {
	key: string;
	default: string[];
};

export const initialModelInput = {
	id: "",
	title: "models",
	tagType: "",
	description: "",
	path: "",
	component: React.createElement("div"),
};

const measureUnitsInitialValue: measureUnitsType = {
	key: "measureUnits",
	default: {
		currency: "USD",
		packDosage: "mg" as PackDosageUnit,
		patientWeight: "kg",
	},
};

const initialValueSimulationsActivePage: ActivePage = {
	key: "simulations.activePage",
	default: 0,
};

const initialSimulationsRowsPerPage: RowsPerPage = {
	key: "simulations.rowsPerPage",
	default: pageSizeOptions[0],
};

const initialModelInputs: modelInputsType = {
	key: "modelInputs",
	default: [initialModelInput],
};

const initialLockedSections: lockedSectionsType = {
	key: "lockedSections",
	default: {
		standardInputs: [],
		sections: [],
	},
};

const initialEvaluationThresholdsInputs = {
	key: "evaluationThresholdsInputs",
	default: [{}],
};

const initialSimulationCalculatedFormValues = {
	key: "simulationCalculatedFormValues",
	default: {},
};

const initialComments: commentInputsType = {
	key: "commentInputs",
	default: {},
};

const initialCachedModels: CachedModelType = {
	key: "cachedModel",
	default: [],
};

const initialSimulatorOutput: SimulatorOutputType = {
	key: "simulatorOutput",
	default: {
		simulationOutput: undefined,
		filteredSimulationOutput: undefined,
	},
};

const initialDiscontinuation: DiscontinuationType = {
	key: "isDiscontinuationDisabled",
	default: false,
};

const initialHideElementState = {
	key: "hideElementState",
	default: false,
};

const initialVariableSnackbar = {
	key: "variableSnackbar",
	default: false,
};

const initialSettingsUpdatedValue = {
	key: "isSettingsUpdated",
	default: false,
};

const isExistingSimulationState = {
	key: "isExistingSimulationState",
	default: false,
};

const roundingValue: RoundingType = {
	key: "rounding",
	// cannot be RoundingOptionLabels.NO_ROUNDING because of https://github.com/kulshekhar/ts-jest/pull/308/files
	default: "outputs-rounding-none" as RoundingOptionLabels.NO_ROUNDING,
};

const initialErrorSnackbarState: SnackbarUpdateStatus = {
	key: "snackbarUpdateStatus",
	default: {
		hasError: false,
		simulationId: [],
	},
};

const initialComparisonFilters: ComparisonFiltersType = {
	key: "comparisonFilters",
	default: { duration: null, calendarYear: null, indications: [] },
};

const initialComparisonSimulations: ComparisonSimulationsType = {
	key: "comparisonSimulations",
	default: [],
};

export const measureUnits = atom(measureUnitsInitialValue);
export const areSettingsUpdated = atom(initialSettingsUpdatedValue);
export const simulationsActivePage = atom(initialValueSimulationsActivePage);
export const simulationsRowsPerPage = atom(initialSimulationsRowsPerPage);
export const modelInputs = atom(initialModelInputs);
export const lockedFields = atom(initialLockedSections);
export const commentsInputs = atom(initialComments);
export const cachedLibraryModels = atom(initialCachedModels);
export const simulatorOutput = atom(initialSimulatorOutput);
export const isDiscontinuationDisabled = atom(initialDiscontinuation);
export const hideElementStateForExport = atom(initialHideElementState);
export const rounding = atom<RoundingOptionLabels>(roundingValue);
export const variableSnackbar = atom(initialVariableSnackbar);
export const snackbarUpdateStatus = atom(initialErrorSnackbarState);
export const comparisonFilters = atom(initialComparisonFilters);
export const comparisonSimulations = atom(initialComparisonSimulations);
export const simulationStatus = atom(isExistingSimulationState);
export const evaluationThresholdsInputs = atom(initialEvaluationThresholdsInputs);
export const simulationCalculatedFormValues = atom(initialSimulationCalculatedFormValues);
